import React, { useState, useMemo } from 'react';
import { 
  ComputerDesktopIcon, 
  GlobeAltIcon, 
  DevicePhoneMobileIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useActiveSessions } from '@/hooks/useSessionManager';
import { UserSession } from '@/services/sessionsService';

const ActiveSessionsPage: React.FC = () => {
  const {
    sessions,
    loading,
    error,
    loadSessions,
    terminateSession,
    terminateAllOtherSessions,
    cleanupExpiredSessions
  } = useActiveSessions();

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Pagination constants
  const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
  
  // Paginated sessions
  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sessions.slice(startIndex, endIndex);
  }, [sessions, currentPage, itemsPerPage]);
  
  // Pagination info
  const totalPages = Math.ceil(sessions.length / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, sessions.length);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleTerminateSession = async (sessionId: string) => {
    setActionLoading(sessionId);
    try {
      await terminateSession(sessionId);
    } catch (error) {
      console.error('Erreur lors de la terminaison de la session:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTerminateAllOthers = async () => {
    setActionLoading('all');
    try {
      await terminateAllOtherSessions();
    } catch (error) {
      console.error('Erreur lors de la terminaison des sessions:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCleanupExpired = async () => {
    setActionLoading('cleanup');
    try {
      await cleanupExpiredSessions();
    } catch (error) {
      console.error('Erreur lors du nettoyage des sessions:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'desktop':
        return <ComputerDesktopIcon className="h-6 w-6 text-blue-600" />;
      case 'mobile':
        return <DevicePhoneMobileIcon className="h-6 w-6 text-blue-600" />;
      case 'tablet':
        return <DevicePhoneMobileIcon className="h-6 w-6 text-blue-600" />;
      default:
        return <GlobeAltIcon className="h-6 w-6 text-blue-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des sessions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
          <button
            onClick={loadSessions}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sessions Actives</h1>
        <p className="text-gray-600">Gérez vos sessions connectées sur différents appareils</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Sessions en cours</h2>
            <span className="text-sm text-gray-500">({sessions.length} session{sessions.length > 1 ? 's' : ''})</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleCleanupExpired}
              disabled={actionLoading === 'cleanup'}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {actionLoading === 'cleanup' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircleIcon className="h-4 w-4 mr-2" />
              )}
              Nettoyer les sessions expirées
            </button>
            <button
              onClick={handleTerminateAllOthers}
              disabled={actionLoading === 'all'}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {actionLoading === 'all' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <TrashIcon className="h-4 w-4 mr-2" />
              )}
              Terminer toutes les autres sessions
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {paginatedSessions.length === 0 ? (
            <div className="text-center py-8">
              <GlobeAltIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{sessions.length === 0 ? 'Aucune session active trouvée' : 'Aucune session sur cette page'}</p>
            </div>
          ) : (
            paginatedSessions.map((session: UserSession) => (
              <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {getDeviceIcon(session.device_type)}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{session.device_display_name}</h3>
                    <p className="text-sm text-gray-500">{session.location} • {session.ip_address}</p>
                    <p className="text-sm text-gray-400">{session.time_since_activity}</p>
                    {session.is_expired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mt-1">
                        Expirée
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {session.is_current_session && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Session actuelle
                    </span>
                  )}
                  {!session.is_current_session && (
                    <button
                      onClick={() => handleTerminateSession(session.id)}
                      disabled={actionLoading === session.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Terminer cette session"
                    >
                      {actionLoading === session.id ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      ) : (
                        <TrashIcon className="h-5 w-5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {sessions.length > 0 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="items-per-page" className="text-sm text-gray-700">
                  Afficher:
                </label>
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ITEMS_PER_PAGE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-700">par page</span>
              </div>
              <div className="text-sm text-gray-700">
                Affichage de {startItem} à {endItem} sur {sessions.length} session(s)
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        currentPage === pageNum
                          ? 'bg-orange-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Sécurité des sessions</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Terminez les sessions sur les appareils que vous n'utilisez plus pour maintenir la sécurité de votre compte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveSessionsPage;
